import * as React from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import { IUnitOfMeasureDefaultFormValues, IUnitOfMeasureFormValues, unitOfMeasureFormSchema } from '../../../../@types/model/masterData/unitOfMeasure/unitOfMeasureFormValues';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { IOptionType } from '../../../../@types/helper';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';

interface IUnitOfMeasureFormProps {
    isLoading : boolean;
    initialValues : IUnitOfMeasureFormValues | IUnitOfMeasureDefaultFormValues;
    onSubmit : (values : IUnitOfMeasureFormValues) => void;
    onClose : () => void;
}

const UnitOfMeasureForm = (props : IUnitOfMeasureFormProps) : React.ReactElement => {
    const schema = unitOfMeasureFormSchema;

    const { handleSubmit, control, formState: { isValid, isDirty } } = useForm<IUnitOfMeasureFormValues>({
        defaultValues: props.initialValues,
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    const { initialValues, onSubmit, onClose, isLoading } = props;

    const useUnitOfMeasureTypeOptions = () : Array<IOptionType> => {
        const unitOfMeasureTypes = useAppSelector(state => state.masterData.unitOfMeasureTypes);
        return useMemo(() => {
            return unitOfMeasureTypes?.filter(x => x.isActive).map(x => ({ label: x.name, value: x.id })) ?? [];
        }, [unitOfMeasureTypes]);
    };
    const unitOfMeasureTypeOptions = useUnitOfMeasureTypeOptions();

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className={'fdc p10 pt15 pb10'}>
                    <div className={'fdc aic'}>
                        <ControlledTextField
                            className={'mb10 w500'}
                            name={'code'}
                            label={'Code'}
                            control={control}
                            defaultValue={initialValues.code}
                        />
                        <ControlledTextField
                            className={'mb10 w500'}
                            name={'name'}
                            label={'Name'}
                            control={control}
                            defaultValue={initialValues.name}
                        />
                        <div style={{ width: '520px', maxWidth: '520px' }}>
                            <ControlledMuiAutoCompleteSelect
                                className={'mb10 w500'}
                                name={'unitOfMeasureType'}
                                label={'Unit Of Measure Type'}
                                control={control}
                                options={unitOfMeasureTypeOptions}
                                defaultValues={initialValues.unitOfMeasureType ?? null}
                            />
                        </div>
                        <div className={'fdr jcfs mb5'}>
                            <ControlledLabelledCheckBox
                                className={'p0'}
                                name={'isActive'}
                                label={'Is Active'}
                                control={control}
                                defaultValue={initialValues.isActive}
                            />
                        </div>
                        <div className={'fdr flx1 aic jcc mt10'}>
                            <Button
                                className={'cpd mr20'}
                                type={'reset'}
                                onClick={onClose}
                            >
                                CANCEL
                            </Button>
                            <PillButton
                                className={'pl30 pr30'}
                                text={'SAVE'}
                                size={'small'}
                                type={'submit'}
                                color={'secondary'}
                                disabled={!isDirty || !isValid || isLoading}
                            />
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default UnitOfMeasureForm;
